import { Link, useRouter, notFound, useLoaderData, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { fetchCollection, type ShopifyProduct, type ShopifyCollection } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";
import { FilterBar } from "@/components/FilterBar";
import { useT, useLocale } from "@/lib/i18n";
import { LocaleLink } from "@/components/LocaleLink";
import { breadcrumbJsonLd, collectionPageJsonLd, localizedHead, SITE_URL } from "@/lib/seo";
import { getCategoryCopy } from "@/lib/category-copy";
import { fetchVirtualCollection } from "@/lib/virtual-collections";
import { trackCollectionView, trackProductListView } from "@/lib/analytics";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import { FaqBlock } from "@/components/seo/FaqBlock";
import { CollectionStory } from "@/components/collection/CollectionStory";
import { getCollectionStory } from "@/components/collection/collection-stories";

export const collectionLoader = async ({ params }: { params: { handle: string } }) => {
  const collection =
    (await fetchVirtualCollection(params.handle)) ?? (await fetchCollection(params.handle, 250));
  if (!collection) throw notFound();
  return { collection };
};

type LoaderData = { collection: ShopifyCollection & { products: { edges: ShopifyProduct[] } } };

function buildHead(
  loaderData: LoaderData | undefined,
  params: { handle?: string } | undefined,
  locale: "da" | "en",
) {
  const c = loaderData?.collection;
  const handle = params?.handle ?? "";
  const path = locale === "en" ? `/en/collections/${handle}` : `/collections/${handle}`;
  const url = `${SITE_URL}${path}`;
  const da = handle ? getCategoryCopy(handle, "da") : null;
  const en = handle ? getCategoryCopy(handle, "en") : null;
  const localeCopy = locale === "en" ? en : da;

  // Build a richer description: prefer the long-form intro from category-copy,
  // fall back to Shopify's collection description, then to a generic line.
  // Trim to ~158 chars so Google doesn't truncate.
  const fallback =
    locale === "en"
      ? `Vintage and second-hand at Farveladeland — ${c?.title ?? "collection"}. Hand-picked in Copenhagen, shipped worldwide.`
      : `Vintage og loppefund hos Farveladeland — ${c?.title ?? "kollektion"}. Hånd-plukket i København, sendes til hele verden.`;
  const rawDescription =
    localeCopy?.metaDescription ||
    localeCopy?.intro ||
    (c?.description && c.description.trim().length > 30 ? c.description : "") ||
    fallback;
  const description =
    rawDescription.length > 158
      ? rawDescription.slice(0, 155).trimEnd() + "…"
      : rawDescription;

  // Title — include "vintage" + "Farveladeland" so it ranks for the brand+keyword
  // combo, but stay under ~60 chars Google shows in the SERP.
  const baseTitle = c?.title ?? (locale === "en" ? "Collection" : "Kollektion");
  const titleSuffix = locale === "en" ? "Vintage at Farveladeland" : "Vintage hos Farveladeland";
  const fullTitle = `${baseTitle} — ${titleSuffix}`;
  const title =
    localeCopy?.metaTitle ??
    (fullTitle.length > 62 ? `${baseTitle} — Farveladeland` : fullTitle);

  // OG image — prefer the collection's image, fall back to the first product
  // image so the share card never ends up blank.
  const ogImage =
    c?.image?.url ||
    (c?.products?.edges?.[0] as ShopifyProduct | undefined)?.node?.images?.edges?.[0]?.node?.url ||
    undefined;

  const ld = c
    ? collectionPageJsonLd({
        name: c.title,
        description,
        url,
        image: ogImage,
      })
    : null;
  const crumb = c
    ? breadcrumbJsonLd([
        { name: locale === "en" ? "Home" : "Forsiden", url: SITE_URL },
        {
          name: locale === "en" ? "Collections" : "Kollektioner",
          url: `${SITE_URL}${locale === "en" ? "/en/collections" : "/collections"}`,
        },
        { name: c.title, url },
      ])
    : null;
  const loc = localizedHead({ path: `/collections/${handle}`, locale });
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      ...(ogImage ? [{ property: "og:image", content: ogImage }] : []),
      ...(ogImage ? [{ name: "twitter:image", content: ogImage }] : []),
      ...loc.meta,
    ],
    links: loc.links,
    scripts: [
      ...(ld ? [{ type: "application/ld+json", children: JSON.stringify(ld) }] : []),
      ...(crumb ? [{ type: "application/ld+json", children: JSON.stringify(crumb) }] : []),
    ],
  };
}

export const collectionHeadDa = ({ loaderData, params }: any) =>
  buildHead(loaderData, params, "da");
export const collectionHeadEn = ({ loaderData, params }: any) =>
  buildHead(loaderData, params, "en");

export function CollectionErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  const { t } = useT();
  return (
    <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
      <p className="eyebrow">{t("collection.error.eyebrow")}</p>
      <h1 className="mt-4 font-display text-5xl">{t("collection.error.title")}</h1>
      {import.meta.env.DEV && (
        <p className="mt-4 text-sm text-muted-foreground">{error.message}</p>
      )}
      <button
        onClick={() => {
          router.invalidate();
          reset();
        }}
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-background hover:bg-terracotta"
      >
        {t("collection.error.cta")}
      </button>
    </div>
  );
}

export function CollectionNotFoundComponent() {
  const { t } = useT();
  return (
    <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
      <p className="eyebrow">{t("collection.notFound.eyebrow")}</p>
      <h1 className="mt-4 font-display text-5xl">{t("collection.notFound.title")}</h1>
      <p className="mt-4 text-sm text-muted-foreground">{t("collection.notFound.body")}</p>
      <LocaleLink
        to="/collections"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-background hover:bg-terracotta"
      >
        {t("collection.notFound.cta")}
      </LocaleLink>
    </div>
  );
}

export function CollectionPage() {
  const { collection } = useLoaderData({ strict: false }) as LoaderData;
  const { t, tn } = useT();
  const locale = useLocale();
  const products = collection.products.edges;
  const [filtered, setFiltered] = useState<ShopifyProduct[]>(products);
  const onFilterChange = useCallback(
    (next: ShopifyProduct[]) => setFiltered(next),
    [],
  );
  const params = useParams({ strict: false }) as { handle?: string };
  const handle = params.handle ?? "";
  useScrollDepth(`collection:${handle}`);
  const copy = getCategoryCopy(handle, locale);
  // The editorial story layout is Danish-only; other locales keep the generic block.
  const story = locale === "da" ? getCollectionStory(handle) : null;
  // Original farveladeland.com layout: first paragraph of the long-form body
  // sits under the title (above the grid); the remaining paragraphs sit in the
  // bottom SEO block. If no long-form body exists, fall back to the short
  // hand-written intro field.
  const bodyParas = (copy?.seo.body ?? "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const introPara =
    bodyParas[0] && !bodyParas[0].startsWith("## ")
      ? bodyParas[0]
      : (copy?.intro ?? t("collection.fallbackIntro"));
  const restParas = bodyParas[0] === introPara ? bodyParas.slice(1) : bodyParas;
  const intro = story ? (copy?.intro ?? introPara) : introPara;


  // Emit a collection_viewed event for the custom Shopify pixel.
  useEffect(() => {
    trackCollectionView({
      id: collection.id,
      handle: collection.handle,
      title: collection.title,
      productCount: products.length,
    });
    trackProductListView({
      list_id: `collection:${collection.handle}`,
      list_name: collection.title,
      item_count: products.length,
    });
  }, [collection.id, collection.handle, collection.title, products.length]);

  return (
    <>
      <section className="pt-36 pb-10 md:pt-44 md:pb-14">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <LocaleLink
            to="/collections"
            className="text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-terracotta"
          >
            {t("collection.back")}
          </LocaleLink>

          <div className="mt-8 grid gap-10 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-7">
              <p className="eyebrow">{t("collection.eyebrow")}</p>
              <h1 className="mt-3 font-display text-[clamp(2.5rem,6.5vw,5.5rem)] leading-[0.98] tracking-tight">
                {copy?.heading ?? collection.title}
              </h1>
              <p className="mt-4 text-xs uppercase tracking-[0.22em] text-muted-foreground">
                {tn("collection.piece", products.length)}
              </p>
            </div>
            <div className="flex flex-col gap-4 md:col-span-5 md:justify-end">
              {intro.split(/\n\s*\n/).map((para, i) => (
                <p
                  key={i}
                  className="text-pretty text-base leading-relaxed text-muted-foreground md:text-[17px]"
                >
                  {para}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pb-20 md:px-10 md:pb-28">
        {products.length === 0 ? (
          <div className="border-y border-border py-24 text-center">
            <p className="font-display text-2xl text-foreground/70">
              {t("collection.empty")}
            </p>
            <LocaleLink
              to="/collections"
              className="mt-6 inline-flex rounded-full bg-foreground px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-background hover:bg-terracotta"
            >
              {t("collection.browseOther")}
            </LocaleLink>
          </div>
        ) : (
          <>
            <FilterBar products={products} onChange={onFilterChange} />
            {filtered.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                {t("filter.noMatch")}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-5 gap-y-12 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 lg:gap-x-8">
                {filtered.map((p: ShopifyProduct, i) => (
                  <ProductCard
                    key={p.node.id}
                    product={p}
                    listId={`collection:${collection.handle}`}
                    listName={collection.title}
                    index={i}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <FaqBlock
        eyebrow={t("faq.eyebrow")}
        title={copy?.faqTitle ?? `${t("faq.titleA")} ${t("faq.titleB")}`}
        items={
          copy?.faq ?? [
            { q: t("faq.q1.q"), a: t("faq.q1.a") },
            { q: t("faq.q3.q"), a: t("faq.q3.a") },
            { q: t("faq.q4.q"), a: t("faq.q4.a") },
            { q: t("faq.q5.q"), a: t("faq.q5.a") },
          ]
        }
      />

      {story && <CollectionStory content={story} bullets={copy?.seo.bullets ?? []} />}

      {copy && !story && (
        <section className="border-t border-border bg-cream/40">
          <div className="mx-auto max-w-[1500px] px-6 py-20 md:px-10 md:py-28">
            <div className="grid gap-12 md:grid-cols-12 md:gap-20">
              <div className="md:col-span-4">
                <p className="eyebrow">{t("collection.about")}</p>
                <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-4xl">
                  {copy.seo.heading}
                </h2>
              </div>

              <div className="md:col-span-8">
                <div className="space-y-5 text-[15px] leading-[1.75] text-foreground/80 md:text-base">
                  {restParas.map((para, i) => {
                    if (para.startsWith("## ")) {
                      return (
                        <h3
                          key={i}
                          className="mt-8 font-display text-xl leading-tight text-foreground first:mt-0 md:text-2xl"
                        >
                          {para.slice(3).trim()}
                        </h3>
                      );
                    }
                    return (
                      <p key={i} className="text-pretty">
                        {para}
                      </p>
                    );
                  })}
                </div>


                {copy.seo.bullets && copy.seo.bullets.length > 0 && (
                  <div className="mt-10 border-t border-border/60 pt-8">
                    <p className="eyebrow">{t("collection.highlights")}</p>
                    <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                      {copy.seo.bullets.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-3 text-sm leading-relaxed text-foreground/85"
                        >
                          <span
                            aria-hidden
                            className="mt-[0.55rem] h-1 w-3 flex-shrink-0 bg-terracotta"
                          />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-10 flex flex-wrap gap-3">
                  <LocaleLink
                    to="/collections"
                    className="inline-flex items-center gap-2 rounded-full border border-foreground/30 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-foreground hover:bg-foreground hover:text-background"
                  >
                    {t("collection.browseOther")}
                  </LocaleLink>
                  <LocaleLink
                    to="/visit"
                    className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-background hover:bg-terracotta"
                  >
                    {t("topbar.book")}
                  </LocaleLink>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

void Link;
